import { useEffect, useState } from "react";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";
import { getBlobDurationInSecs } from "@/utils/blob-utils";
import { changeFileType } from "@/utils/files";
import { useMobile } from "@/hooks/use-responsive";
import {
  DatasetDurationManager,
  DatasetSizeManager,
} from "@/services/license/base/license-manager-abstractions";
import { useSubscriptionLimitationsContext } from "@/providers/subscription-limitations-provider/subscription-limitations-provider";

export const useLocalFileUpload = <
  T extends DatasetSizeManager & DatasetDurationManager,
>(
  changeContext: (file: any) => Promise<void>,
  deleteInContext: () => void,
  licenseManager: T,
) => {
  const [fileUrl, setFileUrl] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [localFile, setLocalFile] = useState<File | null>(null);
  const [localFileName, setLocalFileName] = useState("");
  const { license, user } = useSubscriptionLimitationsContext();
  const { showModal } = useGlobalModalContext();
  const { isMobile } = useMobile();

  useEffect(() => {
    if (localFile) {
      if (isMobile) {
        changeFileType(localFile, "audio/mpeg")
          .then((newFile) => {
            const newUrl = URL.createObjectURL(newFile);
            setFileUrl(newUrl);

            return () => {
              URL.revokeObjectURL(newUrl);
            };
          })
          .catch((error) => {
            console.error("Error changing file type:", error);
          });
      }
      const newUrl = URL.createObjectURL(localFile);
      setFileUrl(newUrl);

      return () => {
        URL.revokeObjectURL(newUrl);
      };
    }
  }, [localFile]);

  const showFileLimitModal = (
    limitationType: "size" | "duration",
    limit: number,
  ) =>
    showModal({
      type: "file_limited_modal",
      props: {
        licenseName: license,
        limitationType: limitationType,
        limit: limit,
      },
    });

  const tryUploadFile = async (file: File) => {
    const duration = (await getBlobDurationInSecs(file)) as number;

    const isDurationLimitedResult =
      await licenseManager.datasetDurationLimiter.isDatasetDurationLimited(
        user?.id,
        license,
        duration,
      );
    if (isDurationLimitedResult.isLimited) {
      showFileLimitModal("duration", isDurationLimitedResult.limit);
      return;
    }

    const isSizeLimitedResult =
      await licenseManager.datasetSizeLimiter.isDatasetSizeLimited(
        user?.id,
        license,
        file.size,
      );
    if (isSizeLimitedResult.isLimited) {
      showFileLimitModal("size", isSizeLimitedResult.limit);
      return;
    }

    await changeContext(file);
  };

  const handleDeleteButtonClick = () => {
    setLocalFile(null);
    setLocalFileName("");
    deleteInContext();
  };

  const handleDropCancel = (event: any) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleFileChange = async (event: any) => {
    const file = event.target.files ? event.target.files[0] : null;
    await tryUploadFile(file);
  };

  const handleDrop = async (event: any) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    await tryUploadFile(file);
    setIsDragging(false);
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
  };

  const handleDragEnter = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  return {
    isDragging,
    localFile,
    localFileName,
    fileUrl,
    setLocalFile,
    setLocalFileName,
    handleDropCancel,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleDeleteButtonClick,
    handleDragEnter,
    handleDragLeave,
  };
};
